import { SESSION_TYPE } from '@hedepy/common/enums/SESSION'
import type { TherapistListRequest } from '@hedepy/common/schemas/routes/api/v2/therapist'

import { CoercedNumberArraySchema } from '@/legacy-src/schemas/CoercedNumberArraySchema'

import { SessionTypeFilter } from '@/src/domains/therapist-filters/constants'
import { parseTherapistSessionLocationFilterValue } from '@/src/domains/therapist-filters/utils/parseTherapistSessionLocationFilterValue'
import { parseTherapistSessionTypeFilterValue } from '@/src/domains/therapist-filters/utils/parseTherapistSessionTypeFilterValue'

const APIsessionTypeMap: Record<SessionTypeFilter, SESSION_TYPE | undefined> = {
  [SessionTypeFilter.ALL]: undefined,
  [SessionTypeFilter.ONLINE]: SESSION_TYPE.ONLINE,
  [SessionTypeFilter.OFFLINE]: SESSION_TYPE.OFFLINE,
  [SessionTypeFilter.HYBRID]: SESSION_TYPE.HYBRID,
}

const getSession = ({
  filterSessionLocationValue,
  filterSessionTypeValue,
}: {
  filterSessionTypeValue: string[]
  filterSessionLocationValue: string[]
}): TherapistListRequest['session'] => {
  const sessionTypeFilter = parseTherapistSessionTypeFilterValue(filterSessionTypeValue[0])

  const type = APIsessionTypeMap[sessionTypeFilter as SessionTypeFilter]

  if (type === undefined) {
    return undefined
  }

  if (type === SESSION_TYPE.ONLINE) {
    return {
      type,
    }
  }

  const officeDistrictIds = parseTherapistSessionLocationFilterValue(filterSessionLocationValue)

  return {
    type,
    officeDistrictIds: officeDistrictIds.length ? officeDistrictIds : undefined,
  }
}

const getAvailability = ({
  filterDaysValue,
  filterInDaysValue,
  filterTimesValue,
}: {
  filterDaysValue: string[]
  filterTimesValue: string[]
  filterInDaysValue: string[]
}): TherapistListRequest['available'] => {
  if (filterInDaysValue.length) {
    return {
      type: 'inDaysCount',
      availableInDaysCount: CoercedNumberArraySchema.parse(filterInDaysValue),
    }
  }
  if (filterDaysValue.length || filterTimesValue.length) {
    return {
      type: 'atTimes',
      times: filterTimesValue,
      days: CoercedNumberArraySchema.parse(filterDaysValue),
    }
  }
  return undefined
}

export const getTherapistListRequestParams = ({
  filterDaysValue,
  filterTimesValue,
  filterInDaysValue,
  filterSessionTypeValue,
  filterSessionLocationValue,
  selectedServiceId,
}: {
  filterTimesValue: string[]
  filterDaysValue: string[]
  filterInDaysValue: string[]
  filterSessionTypeValue: string[]
  filterSessionLocationValue: string[]
  selectedServiceId: number | undefined
}): TherapistListRequest | undefined => {
  const hasFilterParams =
    filterDaysValue.length ||
    filterTimesValue.length ||
    filterInDaysValue.length ||
    filterSessionTypeValue.length ||
    filterSessionLocationValue.length

  if (hasFilterParams && selectedServiceId) {
    return {
      serviceId: selectedServiceId,
      available: getAvailability({ filterDaysValue, filterTimesValue, filterInDaysValue }),
      session: getSession({ filterSessionTypeValue, filterSessionLocationValue }),
    }
  }

  return undefined
}
